<script setup lang="ts">
import addToCart from "./add-to-cart.vue";
import labels from "../../shared/labels";
import { computed, type PropType } from "vue";
import { type ShopProduct } from "./types";
import { createCurrencyFormatter } from "../common/amount-format";
import { format } from "../../../../shared/utils/string";
import { getCheapestFreightMethod } from "../product-specs/delivery-info";
import {
    type PaymentMethodKey,
    type PaymentLogoKey,
} from "../../checkout/payment-type/payment-method";
import { getPaymentMethodLogos } from "../../checkout/payment-type/image-urls";
import { isValidSecondsleft } from "../../../commons/product/auction/countdown";
import CountdownTime from "../auction/countdown-time.vue";
import PaymentCardItem from "./PaymentCardItem.vue";

const formatter = createCurrencyFormatter();

// eslint-disable-next-line no-undef
const props = defineProps({
    product: {
        type: Object as PropType<ShopProduct>,
        required: true,
    },
    paymentKeys: Array as PropType<(PaymentLogoKey | PaymentMethodKey)[]>,
});

const currency = window.odendo.globals.currency;

function formatAmounts(value: number) {
    return formatter.format(value);
}

const cheapestMethod = computed(() => {
    return getCheapestFreightMethod(
        props.product.freightMethods.filter((x) => x.isPickupMethod !== true)
    );
});

const cardPaymentLogos = computed(() => {
    if (props.paymentKeys == null || props.paymentKeys.length == 0) return [];
    return getPaymentMethodLogos(props.paymentKeys);
});
</script>

<template>
    <div>
        <div :class="$style['price-container']">
            <div :class="$style['buy-price']">
                {{ formatAmounts(product.buyPrice) }}
                <div
                    v-if="product.experienceDealsFee != null"
                    :class="$style['buy-price-fee']"
                >
                    {{
                        format(
                            labels.frontendProductpage.specs
                                .experienceDealsFeeSubText,
                            formatAmounts(product.experienceDealsFee)
                        )
                    }}
                </div>
            </div>
            <div
                :class="$style['estimated-price']"
                v-if="
                    product.showEstimatedPrice &&
                    product.estimatedPrice > product.buyPrice
                "
            >
                {{ labels.frontendProductpage.specs.shopEstimatedProductPrice }}
                {{ formatAmounts(product.estimatedPrice) }}
            </div>
        </div>
        <div
            class="discount-badge"
            v-if="
                product.showEstimatedPrice &&
                product.estimatedPrice != null &&
                product.buyPrice != null &&
                product.estimatedPrice > product.buyPrice &&
                Math.round(
                    (1 - product.buyPrice / product.estimatedPrice) * 100
                ) >= 10
            "
        >
            {{
                format(
                    labels.frontendProductList.discount,
                    Math.round(
                        (1 - product.buyPrice / product.estimatedPrice) * 100
                    ) + "%"
                )
            }}
        </div>
        <div
            class="countdown-row"
            v-if="isValidSecondsleft(product.timeTotalSeconds)"
        >
            <div class="countdown-header">
                {{
                    product.timeIsNotStarted
                        ? labels.frontendProductList.productSpecs.headers
                              .timeLeftToStart_shop
                        : labels.frontendProductList.productSpecs.headers
                              .timeLeft_shop
                }}
            </div>
            <countdown-time
                class="countdown-time"
                :seconds-left="product.timeTotalSeconds"
            >
                <template #header> </template>
            </countdown-time>
        </div>
        <div :class="$style['buy-section']">
            <div
                v-if="product.stock != null"
                :class="[$style['stock-status'], 'item-info-line']"
            >
                <i
                    :class="
                        $style['icon-stock-status'] +
                        ' icon-stock-status--' +
                        product.stock.status
                    "
                ></i>

                {{ product.stock.text }}
            </div>
            <div v-if="cheapestMethod != null" class="item-info-line">
                <strong
                    >{{
                        labels.frontendProductpage.shop.freightPriceLabel
                    }}:</strong
                >
                {{ formatAmounts(cheapestMethod.price) }}
            </div>
            <div
                v-if="
                    product.stock != null &&
                    product.stock.count > 0 &&
                    product.freightMethods.some((x) => x.isPickupMethod != true)
                "
                v-html="labels.frontendProductpage.shop.infoItems_delivery"
                class="item-info-line"
            ></div>
            <div
                v-if="
                    product.stock != null &&
                    product.stock.count > 0 &&
                    product.freightMethods.some((x) => x.isPickupMethod != true)
                "
                v-html="labels.frontendProductpage.shop.infoItems_return"
                class="item-info-line"
            ></div>

            <add-to-cart
                :product-id="product.id"
                :stock="product.stock"
                :action="product.addToCartAction"
                class="add-to-cart-comp"
            ></add-to-cart>
            <div class="payment-methods">
                <template v-if="cardPaymentLogos != null">
                    <template v-for="cardPaymentLogo in cardPaymentLogos">
                        <PaymentCardItem :card-payment-logo="cardPaymentLogo" />
                    </template>
                </template>
                <div
                    class="viabill-section"
                    v-if="paymentKeys?.includes('PayEx') == true"
                >
                    <div
                        class="viabill-pricetag"
                        data-view="product"
                        :data-price="product.buyPrice"
                        :data-currency="currency"
                    ></div>
                </div>
            </div>
        </div>
    </div>
</template>

<style module lang="scss">
@import "../../../../responsive/style.scss";

.icon-stock-status {
    width: 12px;
    height: 12px;
    display: inline-block;
    border-radius: 50%;
    margin-right: 5px;
    vertical-align: -1px;
}

.price-container {
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: center;
    margin-top: 20px;

    @include media-breakpoint-up(lg) {
        flex-direction: row;
        justify-content: initial;
        margin-top: 0;
    }
}

.buy-price {
    color: #272727;
    font-family: "Open Sans";
    font-size: 40px;
    font-weight: 800;
    display: inline-block;
    line-height: 1em;
}

.buy-price-fee {
    color: #797a7a;
    font-size: 12px;
    font-weight: normal;
    line-height: 1;
    margin-top: 2px;
    text-align: left;
}

.buy-section {
    margin-top: 20px;
    margin-bottom: 20px;
}

.estimated-price {
    color: #272727;
    font-family: "Open Sans";
    font-size: 14px;
    display: inline-block;
    text-decoration: line-through;
    margin-left: 10px;
}

.stock-status {
    display: block;
    font-weight: bold;
}
</style>

<style scoped lang="scss">
@import "../../../../responsive/style.scss";
@import "vars.scss";

.discount-badge {
    // position: absolute;
    // top: 0;
    // right: 0;
    background-color: #cccccc;
    padding: 5px 14px;
    text-transform: uppercase;
    font-weight: 600;
    display: table;
    margin-left: auto;
    margin-right: auto;
    padding: 8px 12px;
    font-size: 15px;
    font-weight: 500;
    margin-top: 20px;

    @include media-breakpoint-up(lg) {
        flex-basis: auto;
        display: inline-block;
        margin-left: 0;
        margin-right: 0;
    }
}

.countdown-row {
    margin-top: 20px;
    display: flex;
    flex-direction: column;
    align-items: center;
    width: min-content;
    margin-left: auto;
    margin-right: auto;
    @include media-breakpoint-up(lg) {
        align-items: flex-start;
        margin-left: 0;
        margin-right: 0;
    }

    .countdown-header {
        font-weight: 600;
        margin-bottom: 0.5em;
    }
    .countdown-time {
        width: min-content;
    }
}

.add-to-cart-comp {
    flex-basis: 100%;

    @include media-breakpoint-up(lg) {
        flex-basis: auto;
    }
}

.item-info-line + .add-to-cart-comp {
    margin-top: 10px;
}

.viabill-section {
    margin-top: 8px;
    margin-left: $paymentSpace;
    line-height: 12px;
    font-size: 10px;
    color: #666666;
    max-height: 25px;
    flex-grow: 1;
    flex-shrink: 1;
    flex-basis: 50%;
    min-height: 25px;
    height: 25px;
    text-align: center;

    @include media-breakpoint-up(lg) {
        order: 1;
        text-align: left;
        margin-top: 1px;
    }
}
.payment-methods {
    display: flex;
    align-items: center;
    margin-top: 10px;
    flex-wrap: wrap;
    justify-content: center;

    @include media-breakpoint-up(lg) {
        flex-wrap: revert;
        justify-content: flex-start;
    }
}

.payment-type-logo-item-frame:first-child {
    margin-left: 0;
}
.payment-type-logo-item-frame:last-child {
    margin-right: 0px;
}
</style>
